import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { getCookie } from "../../tools/tools";

const AlcoholDistributionCalc = () => {
    const [formData, setFormData] = useState({
        method: 'weight_only_men',
        gender: 'male',
        age: '20',
        weight: '100',
        height: '190',
        error_probability: 0.05
    });

    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const METHOD_CHOICES = [
        ['weight_only_men', 'Watson PE, Watson ID, Batt RD(1986) W'],
        ['weight_height_men', 'Watson PE, Watson ID, Batt RD(1986) HW'],
        ['weight_age_men', 'Watson PE, Watson ID, Batt RD(1986) AW - Males'],
        ['weight_height_age_men', 'Watson PE, Watson ID, Batt RD(1986) AHW'],
        ['weight_height_age_women', 'Watson PE, Watson ID, Batt RD(1986) GAHW'],
        ['bmi_men', 'Forrest A(1986)'],
        ['bmi_women', 'Ulrich L, Cramer Y, Zink P(1987)'],
        ['another_method', 'Seidl S, Jensen U, Alt A(2000)'],
    ];

    const GENDER_CHOICES = [
        ['male', 'Male'],
        ['female', 'Female'],
        ['other', 'Single group'],
    ];

    const validationRules = {
        weight: { min: 30, max: 200 },
        height: { min: 120, max: 210 },
        age: { min: 17, max: 100 },
        error_probability: { min: 0.0001, max: 0.5 },
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Проверка числовых полей
        Object.keys(validationRules).forEach(field => {
            if (formData[field]) {
                const value = parseFloat(formData[field]);
                const rule = validationRules[field];

                if (isNaN(value) || value < rule.min || value > rule.max) {
                    newErrors[field] = `Value must be between ${rule.min} and ${rule.max}`;
                }
            }
        });

        if (!formData.weight) newErrors.weight = "Weight is required";
        if (!formData.error_probability) newErrors.error_probability = "Error probability is required";

        const method = formData.method;
        if ((method === 'weight_height_men' ||
             method === 'bmi_men' ||
             method === 'bmi_women' ||
             method === 'another_method') && !formData.height) {
            newErrors.height = "Height is required for this method";
        }

        if ((method === 'weight_age_men' ||
             method === 'weight_height_age_men' ||
             method === 'weight_height_age_women') && !formData.age) {
            newErrors.age = "Age is required for this method";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const dataToSend = {
                ...formData,
                age: formData.age ? parseFloat(formData.age) : null,
                weight: parseFloat(formData.weight),
                height: formData.height ? parseFloat(formData.height) : null,
                error_probability: parseFloat(formData.error_probability)
            };

            const response = await axios.post('api/alcohol-calcs/2', dataToSend, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': getCookie('csrftoken')
                },
                withCredentials: true,
            });

            setResult(response.data);
            setErrors({});
        } catch (error) {
            console.error('Error occurred:', error);
            if (error.response && error.response.data) {
                setErrors(error.response.data);
            } else {
                setErrors({ general: "An error occurred while processing the request" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="content">
            <div className="section section-components" style={{ paddingTop: 40 }}>
                <div className="container mb-5 pb-100">
                    <h1 className="h1 text-success font-weight-bold mb-4">
                        Reduction Factor Uncertainty Calculator
                    </h1>

                    <form onSubmit={handleSubmit}>
                        <div className="form-group row">
                            <label className="col-sm-4 col-form-label">
                                Calculation Method
                            </label>
                            <div className="col-sm-8">
                                <select
                                    name="method"
                                    className="form-control"
                                    value={formData.method}
                                    onChange={handleChange}
                                >
                                    {METHOD_CHOICES.map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-4 col-form-label">
                                Gender
                            </label>
                            <div className="col-sm-8">
                                <select
                                    name="gender"
                                    className="form-control"
                                    value={formData.gender}
                                    onChange={handleChange}
                                >
                                    {GENDER_CHOICES.map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-4 col-form-label">
                                Weight (kg)
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    name="weight"
                                    className="form-control"
                                    value={formData.weight}
                                    onChange={handleChange}
                                    step="0.1"
                                />
                                {errors.weight && (
                                    <div className="text-danger mt-1">{errors.weight}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-4 col-form-label">
                                Height (cm)
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    name="height"
                                    className="form-control"
                                    value={formData.height}
                                    onChange={handleChange}
                                    disabled={!['weight_height_men', 'bmi_men', 'bmi_women', 'weight_height_age_women','weight_height_age_men','another_method'].includes(formData.method)}
                                />
                                {errors.height && (
                                    <div className="text-danger mt-1">{errors.height}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-4 col-form-label">
                                Age (years)
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    name="age"
                                    className="form-control"
                                    value={formData.age}
                                    onChange={handleChange}
                                    disabled={!['weight_age_men', 'weight_height_age_men', 'weight_height_age_women'].includes(formData.method)}
                                />
                                {errors.age && (
                                    <div className="text-danger mt-1">{errors.age}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group row">
                            <label className="col-sm-4 col-form-label">
                                Error Probability
                            </label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    name="error_probability"
                                    className="form-control"
                                    value={formData.error_probability}
                                    onChange={handleChange}
                                    step="0.01"
                                />
                                {errors.error_probability && (
                                    <div className="text-danger mt-1">{errors.error_probability}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group row">
                            <div className="col-sm-8 offset-sm-4">
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-lg"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Calculating...' : 'Calculate'}
                                </button>
                            </div>
                            <div className="mt-2">
                                <small className="text-muted">
                                    By clicking "Calculate" you agree to the{' '}
                                    <Link to="/agreement">User Agreement</Link>
                                    .
                                </small>
                            </div>
                        </div>
                    </form>
                    
                    {result && (
                        <div className="mt-5">
                            <h2 className="text-muted mb-3">Calculation Results</h2>
                            <div className="card">
                                <div className="card-body">
                                    <h5 className="card-title">Distribution Parameters</h5>
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item">
                                            <strong>Distribution coefficient (r):</strong> {result.r.toFixed(6)}
                                        </li>
                                        <li className="list-group-item">
                                            <strong>Confidence Interval:</strong> ±{result.confidence_interval.toFixed(6)}
                                        </li>
                                        <li className="list-group-item">
                                            <strong>Standard Deviation:</strong> {result.standard_deviation.toFixed(6)}
                                        </li>
                                        <li className="list-group-item">
                                            <strong>Error Probability:</strong> {result.error_probability}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {errors.general && (
                        <div className="alert alert-danger mt-4">
                            {errors.general}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AlcoholDistributionCalc;